import { useRef } from "react";
import QuotationForm from "@/components/quotations/quotation-form/quotation-form";
import QuotationEditClient from "@/components/quotations/quotation-edit-client";
import { createFileRoute } from "@tanstack/react-router";
import { RouteGuardPermissions } from "@/components/permissions/route-guard-multiple";
import { Doc } from "@/convex/_generated/dataModel";

export const Route = createFileRoute("/_authenticated/quotations/new")({
  validateSearch: (search: Record<string, unknown>) => ({
    draft: typeof search.draft === "string" ? search.draft : undefined,
    /**
     * Id local (UUID) de un borrador NUEVO sin materializar (modo CRDT). A
     * diferencia de `draft` (que es el `code` de una cotización YA materializada
     * y abre el edit client), `ldraft` identifica un borrador que solo vive en
     * localStorage/IndexedDB. Lo usa la tabla para reabrir un borrador local y
     * el form lo genera para una creación nueva.
     */
    ldraft: typeof search.ldraft === "string" ? search.ldraft : undefined,
    /**
     * Activa el modo CRDT en el form. Default false — el legacy queda intacto
     * para los usuarios actuales. Ver
     * docs/plan-crdt-phase-3-quotation-form.md.
     *
     * Acepta "1" y 1 — usamos números en vez de booleanos porque TanStack
     * Router descarta los booleanos puros en `navigate({ search: ... })`
     * durante el round-trip parseSearch/validateSearch (observado en AC4).
     */
    crdt:
      search.crdt === "1" ||
      search.crdt === "true" ||
      search.crdt === 1 ||
      search.crdt === true
        ? 1
        : 0,
    /**
     * BETA: si está activo, fuerza que los pushes al server fallen con un error
     * simulado. Sirve para testear el flujo offline (AC4) sin cortar la red
     * real. Ver docs/plan-crdt-phase-3-quotation-form.md sección 9.
     *
     * Mismo motivo que `crdt`: usamos 1/0 en lugar de booleanos.
     */
    "crdt-offline":
      search["crdt-offline"] === "1" ||
      search["crdt-offline"] === "true" ||
      search["crdt-offline"] === 1 ||
      search["crdt-offline"] === true
        ? 1
        : 0,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { draft, ldraft, crdt, "crdt-offline": crdtOffline } = Route.useSearch();
  const isCrdt = !!crdt;
  const isOffline = !!crdtOffline;

  // Capturamos el ?draft SOLO al montar. Si la página se recarga con
  // `?draft=CODE` (borrador ya creado), hidratamos ese borrador vía el edit
  // client. Si es una creación nueva, el form agrega el param in-place después
  // y este ref NO cambia, así el form no se remonta en modo hidratación
  // (evita el flash del skeleton al crear).
  const coldDraftRef = useRef(draft);

  return (
    <RouteGuardPermissions
      permissions={[{ module: "quotations", actions: ["create"] }]}
    >
      {coldDraftRef.current ? (
        <QuotationEditClient
          code={coldDraftRef.current as Doc<"quotations">["code"]}
          mode={isCrdt ? "crdt" : "legacy"}
          forceOffline={isOffline}
        />
      ) : (
        <QuotationForm
          mode={isCrdt ? "crdt" : "legacy"}
          forceOffline={isOffline}
          localDraftId={ldraft}
        />
      )}
    </RouteGuardPermissions>
  );
}
